import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, MessageSquare, Shield, Sparkles, Users, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const Tarifs = () => {
  return (
    <div data-testid="tarifs-page" className="bg-background">
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center hero-gradient noise-overlay overflow-hidden" data-testid="pricing-hero">
        <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-secondary/10 rounded-full blur-3xl" />
        
        <div className="container-asp relative z-10 py-32">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            className="max-w-3xl mx-auto text-center"
          >
            <span className="text-secondary text-sm font-medium tracking-wider uppercase">
              Investissement
            </span>
            <h1 className="text-5xl sm:text-6xl font-bold text-white mt-4 mb-6">
              Une offre<br />
              <span className="text-gradient">sur mesure</span>
            </h1>
            <p className="text-xl text-white/60 leading-relaxed">
              Chaque club est unique. Chaque programme est personnalisé. 
              Nous construisons ensemble la solution adaptée à vos besoins.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="section-padding bg-background" data-testid="philosophy-section">
        <div className="container-asp">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -30 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-white mb-6">
                Pourquoi pas de<br />grille tarifaire ?
              </h2>
              <p className="text-white/60 leading-relaxed mb-6">
                Les solutions génériques produisent des résultats génériques. 
                Notre approche est différente : nous analysons votre contexte, 
                vos objectifs, votre effectif, et nous construisons un programme 
                qui vous est propre.
              </p>
              <p className="text-white/60 leading-relaxed mb-8">
                Le tarif reflète cet investissement dans votre réussite. 
                Il est le fruit d'un échange, pas d'un catalogue.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-white/50">
                  <Check className="w-5 h-5 text-primary" />
                  <span>Analyse personnalisée de vos besoins</span>
                </div>
                <div className="flex items-center gap-3 text-white/50">
                  <Check className="w-5 h-5 text-primary" />
                  <span>Programme adapté à votre effectif</span>
                </div>
                <div className="flex items-center gap-3 text-white/50">
                  <Check className="w-5 h-5 text-primary" />
                  <span>Accompagnement calibré sur vos objectifs</span>
                </div>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 30 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
            >
              <Card className="card-glass p-8 glow-primary">
                <div className="text-center">
                  <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mx-auto mb-6">
                    <MessageSquare className="w-10 h-10 text-white/80" />
                  </div>
                  <h3 className="text-2xl font-semibold text-white mb-4">
                    Parlons de votre projet
                  </h3>
                  <p className="text-white/50 mb-6">
                    Un échange de 30 minutes suffit pour comprendre vos besoins 
                    et vous proposer une solution adaptée.
                  </p>
                  <Link to="/contact">
                    <Button className="btn-secondary w-full group" data-testid="pricing-contact-btn">
                      Prendre rendez-vous
                      <ArrowUpRight className="ml-2 w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </Button>
                  </Link>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="section-padding bg-gradient-to-b from-background to-black/50" data-testid="included-section">
        <div className="container-asp">
          <motion.div 
            initial={{ opacity: 0 }} 
            whileInView={{ opacity: 1 }} 
            viewport={{ once: true }} 
            className="text-center mb-16"
          >
            <span className="text-primary text-sm font-medium tracking-wider uppercase">
              Ce qui est inclus
            </span>
            <h2 className="text-4xl font-bold text-white mt-4">
              Dans chaque programme
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }} 
              transition={{ delay: 0.1 }}
            >
              <Card className="card-glass h-full p-8 hover:border-primary/30 transition-colors">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                  <Sparkles className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Technologie IA</h3>
                <p className="text-white/50 leading-relaxed">
                  Accès complet à notre plateforme d'entraînement audio-cognitif 
                  et à notre application mobile.
                </p>
              </Card>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }} 
              transition={{ delay: 0.2 }}
            >
              <Card className="card-glass h-full p-8 hover:border-secondary/30 transition-colors">
                <div className="w-14 h-14 rounded-2xl bg-secondary/10 flex items-center justify-center mb-6">
                  <Users className="w-7 h-7 text-secondary" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Accompagnement</h3>
                <p className="text-white/50 leading-relaxed">
                  Formation de votre staff, séances terrain, 
                  et suivi personnalisé tout au long de la saison.
                </p>
              </Card>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }} 
              transition={{ delay: 0.3 }}
            >
              <Card className="card-glass h-full p-8 hover:border-accent/30 transition-colors">
                <div className="w-14 h-14 rounded-2xl bg-accent/10 flex items-center justify-center mb-6">
                  <Shield className="w-7 h-7 text-accent" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Garantie résultats</h3>
                <p className="text-white/50 leading-relaxed">
                  Des métriques claires et des objectifs mesurables. 
                  Nous nous engageons sur les résultats.
                </p>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="py-16 bg-black/50 border-y border-white/5" data-testid="trust-section">
        <div className="container-asp">
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16">
            <p className="text-white/30 text-sm uppercase tracking-wider">Ils nous font confiance</p>
            <div className="flex items-center gap-12">
              <img 
                src="https://upload.wikimedia.org/wikipedia/fr/thumb/c/ca/Logo_Union_Bordeaux_B%C3%A8gles.svg/800px-Logo_Union_Bordeaux_B%C3%A8gles.svg.png" 
                alt="UBB" 
                className="h-14 w-auto opacity-40 hover:opacity-70 transition-opacity" 
              />
              <img 
                src="https://upload.wikimedia.org/wikipedia/fr/thumb/8/8e/Logo_Biarritz_olympique_2021.svg/800px-Logo_Biarritz_olympique_2021.svg.png" 
                alt="Biarritz" 
                className="h-14 w-auto opacity-40 hover:opacity-70 transition-opacity" 
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-background relative overflow-hidden" data-testid="pricing-cta">
        <div className="absolute inset-0 hero-gradient" />
        
        <div className="container-asp relative z-10 text-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Prêt à investir dans<br />
              <span className="text-gradient">la performance ?</span>
            </h2>
            <p className="text-white/60 text-lg mb-8 max-w-xl mx-auto">
              Contactez-nous pour discuter de votre projet. 
              Sans engagement, réponse sous 24h.
            </p>
            <Link to="/contact">
              <Button className="btn-secondary text-base px-10 py-4 group" data-testid="pricing-demo-btn">
                S'inscrire sur la liste d'attente
                <ArrowUpRight className="ml-2 w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Tarifs;
